import { useState } from "react";
import { Tab, Nav } from "react-bootstrap";
import Datatable from "../../../components/shared/datatable/Datatable";
import Button from "../../../components/shared/buttons/button";
import useTranslator from "../../../hooks/useTranslator";
import { useGetEnvVariablesQuery } from "./systemVariablesApi";
import EnvVariableModal from "./EnvVariableModal";
import { useGetAllMixingCartQuery, useDeleteMixingCartMutation } from "../../settings/mixingCart/mixingCartApi";
import MixingCartModal from "../../settings/mixingCart/MixingCartModal";
import CommonDeleteModal from "../../../components/shared/modalLayout/CommonDeleteModal";
import { EditSvg } from "../../../svgFiles/EditSvg";
import { DeleteSvg } from "../../../svgFiles/DeleteSvg";
import "../../../components/shared/tabs/tabsComponent.css";

const SystemVariables = () => {
  const { _t } = useTranslator();
  const [activeTab, setActiveTab] = useState("env_variables");

  // ── Env Variables ──────────────────────────────────────────────────────────
  const [envPage, setEnvPage] = useState(1);
  const [showEnvModal, setShowEnvModal] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState(null);
  const env_page_limit = 100;

  const { data: envData, isLoading: envLoading, isFetching: envFetching } =
    useGetEnvVariablesQuery({ page: envPage, per_page: env_page_limit });

  const handleEditEnv = (row) => {
    setSelectedEnv(row);
    setShowEnvModal(true);
  };

  const envColumns = [
    { label: _t("title", "Title"), accessor: "title" },
    // { label: _t("note", "Note"), accessor: "notes" },
    { label: _t("value", "Value"), accessor: "value" },
    {
      label: _t("action", "Action"),
      accessor: "id",
      cell: ({ row }) => (
        <Button size="small" label={_t("edit", "Edit")} onClick={() => handleEditEnv(row)} />
      ),
    },
  ];

  // ── Pasting Cart ───────────────────────────────────────────────────────────
  const [cartPage, setCartPage] = useState(1);
  const cart_page_limit = 100;
  const [showCartModal, setShowCartModal]       = useState(false);
  const [showCartDeleteModal, setShowCartDeleteModal] = useState(false);
  const [selectedCart, setSelectedCart]         = useState(null);
  const [cartToDelete, setCartToDelete]         = useState(null);
  const [cartModalMode, setCartModalMode]       = useState("edit");

  const { data: cartData, isLoading: cartLoading, isFetching: cartFetching } =
    useGetAllMixingCartQuery({ page: cartPage, per_page: cart_page_limit });

  const [deleteMixingCart, { isLoading: isDeletingCart }] = useDeleteMixingCartMutation();

  const handleAddCart = () => {
    setSelectedCart(null);
    setCartModalMode("add");
    setShowCartModal(true);
  };

  const handleEditCart = (row) => {
    setSelectedCart(row);
    setCartModalMode("edit");
    setShowCartModal(true);
  };

  const handleDeleteCart = (row) => {
    setCartToDelete(row);
    setShowCartDeleteModal(true);
  };

  const confirmDeleteCart = async () => {
    setShowCartDeleteModal(false);
    try {
      if (cartToDelete?.id) {
        await deleteMixingCart(cartToDelete.id).unwrap();
      }
    } catch (err) {
      console.error("Error deleting cart:", err);
    } finally {
      setCartToDelete(null);
    }
  };

  const cartColumns = [
    { label: _t("name",           "Name"),           accessor: "name"           },
    { label: _t("empty_weight",   "Empty Weight"),   accessor: "empty_weight"   },
    { label: _t("max_tray_space", "Max Tray Space"), accessor: "max_tray_space" },
    {
      label: _t("edit_delete", "Edit/Delete"),
      accessor: "actions",
      cell: ({ row }) => (
        <>
          <span className="cursor-pointer pe-2" onClick={() => handleEditCart(row)}>
            <EditSvg />
          </span>
          {/* <span className="cursor-pointer" onClick={() => handleDeleteCart(row)}>
            {DeleteSvg}
          </span> */}
        </>
      ),
    },
  ];

  return (
    <div>
      {showEnvModal && (
        <EnvVariableModal
          show={showEnvModal}
          setShow={setShowEnvModal}
          record={selectedEnv}
        />
      )}
      {showCartModal && (
        <MixingCartModal
          show={showCartModal}
          setShow={setShowCartModal}
          record={selectedCart}
          mode={cartModalMode}
        />
      )}
      <CommonDeleteModal
        show={showCartDeleteModal}
        handleClose={() => { setShowCartDeleteModal(false); setCartToDelete(null); }}
        title={_t("confirm_delete", "Confirm Delete")}
        message={`${_t("sure_confirm_delete", "Are you sure you want to delete")} ${cartToDelete?.name}?`}
        onConfirm={confirmDeleteCart}
        isLoading={isDeletingCart}
      />

      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <div className="d-flex flex-column cmn_tabs mb-3">
          <Nav variant="pills">
            <Nav.Item>
              <Nav.Link eventKey="env_variables">
                {_t("env_variables", "Env Variables")}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="pasting_cart">
                {_t("pasting_cart", "Pasting Cart")}
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        <Tab.Content>
          {/* ── Env Variables tab ── */}
          <Tab.Pane eventKey="env_variables">
            <Datatable
              title={_t("env_variables", "Env Variables")}
              columns={envColumns}
              tableData={envData?.data}
              pagination
              pageLimit={env_page_limit}
              page={envPage}
              onPageChange={setEnvPage}
              isLoading={envLoading || envFetching}
            />
          </Tab.Pane>

          {/* ── Pasting Cart tab ── */}
          <Tab.Pane eventKey="pasting_cart">
            <Datatable
              title={_t("pasting_cart", "Pasting Cart")}
              columns={cartColumns}
              tableData={cartData?.data}
              buttons={[
                {
                  label: _t("add_cart", "Add Cart"),
                  onClick: handleAddCart,
                },
              ]}
              pageLimit={cart_page_limit}
              pagination
              page={cartPage}
              onPageChange={setCartPage}
              isLoading={cartLoading || cartFetching}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default SystemVariables;
